import React, { useEffect, useMemo, useState } from "react";

import { MoveLeft, MoveRight, Plus } from "lucide-react";

import {
  ACCOUNT_ROLE_FILTER_OPTIONS,
  AccountMemberDetails,
  AccountTeamCandidate,
  AccountTeamMemberPayload,
  AddMode,
  NewMember,
  buildSubmission,
  defaultBatchName,
  detailsFromCandidate,
  emptyNewMember,
  markExistingMembers,
  filterCandidates,
  indexMembersByEmail,
  validateNewMember,
} from "./accountTeamHelpers";
import Button from "../../../../Components/Button";
import CommonInput from "../../../../Components/Input";
import CommonModal from "../../../../Components/Modal";
import Select from "../../../../Components/Select";
import StepProgressBar from "../../../../Components/StepProgressBar";
import type { AccountTeamMember } from "../../../../Store/Reducers/AccountDetailsSlice";
import {
  ACCOUNT_TEAM_ROLE_OPTIONS,
  getAccountTeamRoleDisplayLabel,
} from "../../../../Utils/Constants/RoleConstants";

interface AddAccountTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountName: string;
  candidates: AccountTeamCandidate[];
  candidatesLoading: boolean;
  existingMembers: AccountTeamMember[];
  approverOptions: { label: string; value: string }[];
  loading: boolean;
  submitError: string | null;
  onSubmit: (payload: {
    batch_name: string | null;
    approver_id: string;
    members: AccountTeamMemberPayload[];
  }) => Promise<boolean>;
}

/**
 * Account-team add flow: one member at a time, and *either* an existing
 * directory user or a brand-new person, never both.
 *
 * Approving someone who already belongs to the org is a cheap decision;
 * approving a new person creates an organization_users row. Keeping the paths
 * apart means an easy approval never queues behind a heavier one.
 */
const AddAccountTeamModal = ({
  isOpen,
  onClose,
  accountName,
  candidates,
  candidatesLoading,
  existingMembers,
  approverOptions,
  loading,
  submitError,
  onSubmit,
}: AddAccountTeamModalProps) => {
  const [mode, setMode] = useState<AddMode>("directory");
  const [step, setStep] = useState<1 | 2>(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newMember, setNewMember] = useState<NewMember>(emptyNewMember());
  const [switchNotice, setSwitchNotice] = useState<string | null>(null);
  const [batchName, setBatchName] = useState("");
  const [approverId, setApproverId] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setMode("directory");
    setStep(1);
    setSearch("");
    setRoleFilter("all");
    setSelectedId(null);
    setNewMember(emptyNewMember());
    setSwitchNotice(null);
    setBatchName(defaultBatchName("directory"));
    setApproverId(approverOptions[0]?.value || "");
  }, [isOpen, approverOptions]);

  const existingByEmail = useMemo(
    () => indexMembersByEmail(existingMembers),
    [existingMembers]
  );

  // Mark candidates who are already on the team (shown but not selectable).
  const markedCandidates = useMemo(
    () => markExistingMembers(candidates, existingByEmail),
    [candidates, existingByEmail]
  );

  // For backward compatibility, keep availableCandidates as the selectable ones only.
  const availableCandidates = useMemo(
    () => markedCandidates.filter(c => !c.isAlreadyAdded),
    [markedCandidates]
  );

  const filteredCandidates = useMemo(
    () => filterCandidates(markedCandidates, search, roleFilter),
    [markedCandidates, roleFilter, search]
  );

  const selectedCandidate = useMemo(
    () =>
      availableCandidates.find(candidate => candidate.user_id === selectedId) ||
      null,
    [availableCandidates, selectedId]
  );

  const issue = useMemo(
    () =>
      validateNewMember(newMember, {
        candidates: availableCandidates,
        existingByEmail,
      }),
    [newMember, availableCandidates, existingByEmail]
  );

  const updateNewMember = (patch: Partial<NewMember>) =>
    setNewMember(prev => ({ ...prev, ...patch }));

  const updateDetails = (patch: Partial<AccountMemberDetails>) =>
    setNewMember(prev => ({
      ...prev,
      details: { ...prev.details, ...patch },
    }));

  const enterNewMode = () => {
    setSwitchNotice(null);
    setBatchName(defaultBatchName("new"));
    setMode("new");
  };

  /** Back to the picker. Anything typed is kept, never silently binned. */
  const backToDirectory = () => {
    setBatchName(defaultBatchName("directory"));
    setMode("directory");
  };

  /**
   * The typed address turned out to belong to an existing org user, so clear
   * the form and pick that person off the list instead.
   */
  const selectFromListInstead = (candidate: AccountTeamCandidate) => {
    setNewMember(emptyNewMember());
    setSelectedId(candidate.user_id);
    setBatchName(defaultBatchName("directory"));
    setMode("directory");
    setSwitchNotice(
      `${candidate.full_name} already belongs to your organization — selected from the list instead.`
    );
  };

  const canReview =
    mode === "directory" ? Boolean(selectedCandidate) : issue.kind === "none";

  const reviewMember = useMemo(() => {
    if (mode === "directory") {
      if (!selectedCandidate) return null;
      return {
        name: selectedCandidate.full_name,
        email: selectedCandidate.email,
        details: detailsFromCandidate(selectedCandidate),
      };
    }
    return {
      name: newMember.name.trim(),
      email: newMember.email.trim(),
      details: newMember.details,
    };
  }, [mode, selectedCandidate, newMember]);

  const handleSubmit = async () => {
    if (loading || !approverId) return;
    const members = buildSubmission(mode, {
      selectedCandidate,
      newMember,
    });
    if (members.length === 0) return;
    const ok = await onSubmit({
      batch_name: batchName.trim() || null,
      approver_id: approverId,
      members,
    });
    if (ok) onClose();
  };

  const newMemberError = () => {
    switch (issue.kind) {
      case "invalid-email":
        return "Please enter a valid email address";
      case "already-on-team":
        return `${issue.member.full_name || "This person"} is already on this account team`;
      case "in-directory":
        return `${issue.candidate.full_name} already belongs to your organization`;
      default:
        return "";
    }
  };

  const detailRow = (label: string, value: string) => (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <span className="text-sm text-gray-900">{value || "—"}</span>
    </div>
  );

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      onCloseButton={onClose}
      title={mode === "new" ? "Add New Member" : "Add Account Team Member"}
      size="3xl"
      showHeader
      contentClassName="overflow-y-auto p-0"
      className="max-h-[92vh]"
      nested
    >
      <div className="border-b border-gray-200 px-5 py-3">
        <StepProgressBar
          steps={[
            { label: mode === "new" ? "Add Member" : "Select Member" },
            { label: "Review & Select Approver" },
          ]}
          currentStepIndex={step}
        />
      </div>

      <div className="space-y-5 px-5 py-5">
        {step === 1 ? (
          <>
            {mode === "directory" ? (
              <>
                <div>
                  <p className="mb-2 text-[0.9375rem] font-semibold text-gray-800">
                    Select from your organization
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <div className="flex-1">
                      <CommonInput
                        name="search-account-member"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search by name or email..."
                      />
                    </div>
                    <div className="w-full sm:w-[220px]">
                      <Select
                        name="account-role-filter"
                        value={roleFilter}
                        options={ACCOUNT_ROLE_FILTER_OPTIONS}
                        onChange={e => setRoleFilter(String(e.target.value))}
                        isShowPlaceHolder={false}
                      />
                    </div>
                  </div>

                  <div className="mt-3 max-h-[280px] overflow-y-auto rounded-lg border border-gray-200">
                    {candidatesLoading ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-500">
                        Loading directory...
                      </div>
                    ) : filteredCandidates.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-500">
                        No matching people found
                      </div>
                    ) : (
                      filteredCandidates.map(candidate => (
                        <label
                          key={candidate.user_id}
                          className={`flex items-start gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 ${
                            candidate.isAlreadyAdded
                              ? "cursor-not-allowed bg-amber-50"
                              : "cursor-pointer"
                          }`}
                        >
                          <input
                            type="radio"
                            name="account-team-candidate"
                            checked={selectedId === candidate.user_id}
                            disabled={candidate.isAlreadyAdded}
                            onChange={() => {
                              if (candidate.isAlreadyAdded) return;
                              setSwitchNotice(null);
                              setSelectedId(candidate.user_id);
                            }}
                            className="mt-1 h-4 w-4 border-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p
                                className={`mb-0 truncate text-[15px] font-semibold ${
                                  candidate.isAlreadyAdded
                                    ? "text-gray-500"
                                    : "text-gray-900"
                                }`}
                              >
                                {candidate.full_name}
                              </p>
                              {candidate.isAlreadyAdded && (
                                <span className="shrink-0 rounded border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                                  Already Added
                                </span>
                              )}
                            </div>
                            <p
                              className={`mb-1 truncate text-sm ${
                                candidate.isAlreadyAdded
                                  ? "text-gray-400"
                                  : "text-gray-500"
                              }`}
                            >
                              {candidate.email}
                            </p>
                            <span
                              className={`rounded px-2 py-0.5 text-xs font-medium ${
                                candidate.isAlreadyAdded
                                  ? "bg-gray-100 text-gray-500"
                                  : "bg-violet-100 text-violet-700"
                              }`}
                            >
                              {getAccountTeamRoleDisplayLabel(
                                candidate.role_name
                              )}
                            </span>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                  {/*
                    A radio cannot be unticked by clicking it again, so without
                    this there is no way back to the new-member path once
                    someone is picked.
                  */}
                  {selectedCandidate ? (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <p className="mb-0 text-sm font-medium text-violet-700">
                        {selectedCandidate.full_name} selected
                      </p>
                      <Button
                        variant="outline-primary"
                        className="px-3 py-1.5 text-xs font-medium"
                        onClick={() => {
                          setSelectedId(null);
                          setSwitchNotice(null);
                        }}
                      >
                        Clear selection
                      </Button>
                    </div>
                  ) : null}
                  {switchNotice ? (
                    <p className="mb-0 mt-2 rounded border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-700">
                      {switchNotice}
                    </p>
                  ) : null}
                </div>

                {/*
                  A new person is a separate approval, so this is offered only
                  while nobody is selected from the directory.
                */}
                {!selectedId ? (
                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="mb-0 text-sm text-gray-600">
                        Member not listed in your organization?
                      </p>
                      <Button
                        variant="outline-primary"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium"
                        onClick={enterNewMode}
                      >
                        <Plus size={14} />
                        Add New Member
                      </Button>
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <p className="mb-0 text-[0.9375rem] font-semibold text-gray-800">
                    New member details
                  </p>
                  <Button
                    variant="outline-primary"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium"
                    onClick={backToDirectory}
                  >
                    <MoveLeft size={14} />
                    Back to list
                  </Button>
                </div>

                <CommonInput
                  label="Member Email"
                  name="member_email"
                  required
                  value={newMember.email}
                  onChange={e => updateNewMember({ email: e.target.value })}
                  placeholder="jane.doe@company.com"
                  error={newMemberError()}
                />
                {issue.kind === "in-directory" ? (
                  <Button
                    variant="outline-primary"
                    className="px-3 py-1.5 text-xs font-medium"
                    onClick={() => selectFromListInstead(issue.candidate)}
                  >
                    Select from list instead
                  </Button>
                ) : null}
                <CommonInput
                  label="Member Name"
                  name="member_name"
                  required
                  value={newMember.name}
                  onChange={e => updateNewMember({ name: e.target.value })}
                  placeholder="Jane Doe"
                />
                <Select
                  label="Role"
                  name="member_role"
                  required
                  value={newMember.details.role}
                  options={ACCOUNT_TEAM_ROLE_OPTIONS}
                  onChange={e =>
                    updateDetails({ role: String(e.target.value) })
                  }
                  isShowPlaceHolder={false}
                />
                <CommonInput
                  label="Member Title"
                  name="member_position"
                  required
                  value={newMember.details.position}
                  onChange={e => updateDetails({ position: e.target.value })}
                  placeholder="Senior Account Executive"
                />
                <CommonInput
                  label="Member Phone"
                  name="member_phone"
                  value={newMember.details.phone}
                  onChange={e => updateDetails({ phone: e.target.value })}
                  placeholder="+1-555-0100"
                />
                <CommonInput
                  label="Languages"
                  name="member_languages"
                  value={newMember.details.languages}
                  onChange={e => updateDetails({ languages: e.target.value })}
                  placeholder="English, Spanish"
                />
              </>
            )}
          </>
        ) : (
          <>
            <div>
              <h4 className="mb-3 text-[0.9375rem] font-semibold text-gray-800">
                Review member for {accountName}
              </h4>
              {reviewMember ? (
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="mb-3">
                    <p className="mb-0 truncate text-base font-semibold text-gray-900">
                      {reviewMember.name}
                    </p>
                    <p className="mb-0 truncate text-sm text-gray-500">
                      {reviewMember.email}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {detailRow(
                      "Role",
                      getAccountTeamRoleDisplayLabel(reviewMember.details.role)
                    )}
                    {detailRow("Member Title", reviewMember.details.position)}
                    {detailRow("Member Phone", reviewMember.details.phone)}
                    {detailRow("Languages", reviewMember.details.languages)}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <CommonInput
                className="mb-4"
                label="Batch name (optional)"
                name="batch_name"
                value={batchName}
                onChange={e => setBatchName(e.target.value)}
                placeholder={defaultBatchName(mode)}
              />
              <Select
                label="Select Approver"
                name="approver_id"
                required
                value={approverId}
                onChange={e => setApproverId(String(e.target.value))}
                options={approverOptions}
                disabled={approverOptions.length === 0}
                isShowPlaceHolder={false}
              />
              <p className="mt-2 text-[13px] text-amber-700">
                {mode === "new"
                  ? "This new user will be created once approved."
                  : "This member will be sent for approval before being added."}
              </p>
            </div>
          </>
        )}

        {submitError ? (
          <p className="text-sm font-medium text-red-600">{submitError}</p>
        ) : null}
      </div>

      <div className="flex items-center justify-between border-t border-gray-200 px-5 py-4">
        {step === 1 ? (
          <>
            <Button
              variant="outline-primary"
              className="inline-flex items-center gap-2"
              onClick={onClose}
            >
              <MoveLeft size={16} />
              Cancel
            </Button>
            <Button
              variant="primary"
              className="inline-flex items-center gap-2"
              onClick={() => setStep(2)}
              disabled={!canReview}
            >
              Review
              <MoveRight size={16} />
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline-primary"
              className="inline-flex items-center gap-2"
              onClick={() => setStep(1)}
            >
              <MoveLeft size={16} />
              Back
            </Button>
            <Button
              variant="primary"
              className="inline-flex items-center gap-2"
              onClick={handleSubmit}
              disabled={loading || !approverId || !reviewMember}
            >
              {loading ? "Submitting..." : "Confirm"}
              {!loading ? <MoveRight size={16} /> : null}
            </Button>
          </>
        )}
      </div>
    </CommonModal>
  );
};

export default AddAccountTeamModal;
